import { g } from "analytics";
import { clamp, P, Pp, Segments, SharedColors } from "apparatus/library/common";
import { UpdateArrowProperties } from "arrows/command";
import { createInteractionHandleFor } from "editing/handle";
import { Key, Path, Point } from "paper";
import * as React from "react";
import { appStore } from "store/store";
import { ChemMark } from "text/parse";
import { AdvancedText } from "text/render";
import { add } from "types";
import { Debug } from "vars";
import { Adjustable, Interactable, SelectAndMove } from "../editing/interaction";
/**
 * React component to draw an arrow, accompanied by a text label.
 * The "start" is one extremity and where the label is placed.
 * The "end" is the other, and is where the arrow head is placed.
 */
export default class ArrowComponent extends React.Component {
    editArrowHead(props, delta) {
        var deltaVectorPosition = add(this.props.end, delta);
        if (Key.modifiers.shift) {
            const arrowStartPoint = Pp(this.props.start);
            const arrowEndPoint = Pp(add(this.props.end, delta));
            const deltaVector = arrowEndPoint.subtract(arrowStartPoint);
            const angle = deltaVector.angle;
            var roundedAngle = ((angle / 45).round()) * 45;
            deltaVector.angle = roundedAngle;
            deltaVectorPosition = add(this.props.start, deltaVector);
        }
        return deltaVectorPosition;
    }
    editArrowTail(props, delta) {
        var deltaVectorPosition = add(this.props.start, delta);
        if (Key.modifiers.shift) {
            const arrowStartPoint = Pp(add(this.props.start, delta));
            const arrowEndPoint = Pp(this.props.end);
            const deltaVector = arrowStartPoint.subtract(arrowEndPoint);
            const angle = deltaVector.angle;
            var roundedAngle = ((angle / 45).round()) * 45;
            deltaVector.angle = roundedAngle;
            deltaVectorPosition = add(this.props.end, deltaVector);
        }
        return deltaVectorPosition;
    }
    constructor(props) {
        super(props);
        /**
         * The last zoom level for which the UI elements, such as interaction handles, were updated.
         */
        this.lastUpdatedUiForZoom = -1;
        /** Text cache. */
        this.textCache = {
            mode: "basic",
            input: "",
            result: { segments: [] }
        };
        var start = new Point(props.start);
        this.advancedText = AdvancedText.withInteraction(SelectAndMove(this));
        this.textStyle = {
            fontFamily: "sans-serif",
            fontSize: this.props.fontSize,
            textAlign: this.props.textAlign,
            fillColor: "black",
        };
        this.line = new Path([start, start]);
        this.line.strokeWidth = 3.0;
        this.line.selectedColor = SharedColors.selectionGreen;
        this.hittableLine = new (Interactable(Path, SelectAndMove(this)))([start, start]);
        this.hittableLine.strokeWidth = 20.0;
        this.hittableLine.strokeColor = "#ffffff20";
        this.arrowHead = this.createArrowHead();
        this.arrowHead.applyMatrix = false;
        // Create an interaction handler for adjusting the head.
        this.arrowHeadHint = createInteractionHandleFor(Adjustable({
            setDelta: (delta) => {
                this.setState({ end: this.editArrowHead(props, delta) });
            },
            onEnd: (delta) => {
                g("UpdateAppearance", "Arrow::{driver}_head");
                return new UpdateArrowProperties([{
                        id: this.id,
                        value: { start: this.props.start, end: this.editArrowHead(props, delta) },
                        previousValue: { start: this.props.start, end: this.props.end },
                    }]);
            },
        }));
        // Create an interaction handler for adjusting the tail.
        this.arrowTailHint = createInteractionHandleFor(Adjustable({
            setDelta: (delta) => {
                this.setState({ start: this.editArrowTail(props, delta) });
            },
            onEnd: (delta) => {
                g("UpdateAppearance", "Arrow::{driver}_start");
                return new UpdateArrowProperties([{
                        id: this.id,
                        value: { start: this.editArrowTail(props, delta), end: this.props.end },
                        previousValue: { start: this.props.start, end: this.props.end },
                    }]);
            },
        }));
        this.state = {
            start: this.props.start,
            end: this.props.end,
            label: this.props.label,
            fontSize: this.props.fontSize,
            textColor: this.props.textColor,
            arrowThickness: this.props.arrowThickness,
        };
    }
    get currentPosition() {
        return Pp(this.props.end).add(Pp(this.props.start)).divide(2.0);
    }
    get bounds() {
        if (this.props.showArrow) {
            return this.line.bounds.unite(this.advancedText.bounds);
        }
        // Without arrow, only the text forms the bounding box.
        return this.advancedText.bounds;
    }
    get alignmentBounds() {
        var _a;
        // For alignment, only use the text's bounds.
        // Use the special alignment bounds, which ignore the subscript etc.
        // Fallback on normal bounds, sometimes alignmentBounds can be undefined for some reason?
        // https://sentry.io/share/issue/2c94edea5df7495f8ed74e31c23bf496/
        return (_a = this.advancedText.alignmentBounds) !== null && _a !== void 0 ? _a : this.advancedText.bounds;
    }
    get parentGroup() {
        return this.props.parentGroup;
    }
    get id() {
        return this.props.id;
    }
    get isSelected() {
        return this.props.selected;
    }
    get currentRotation() {
        return 0;
    }
    setPositionDelta(delta) {
        this.setState({
            start: add(this.props.start, delta),
            end: add(this.props.end, delta)
        });
    }
    setRotationDelta(delta, pivot) {
        let newStart = Pp(this.props.start).rotate(delta, pivot);
        let newEnd = Pp(this.props.end).rotate(delta, pivot);
        this.setState({
            start: newStart,
            end: newEnd,
        });
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            start: nextProps.start,
            end: nextProps.end,
            label: nextProps.label,
            fontSize: nextProps.fontSize,
            textColor: nextProps.textColor,
            arrowThickness: nextProps.arrowThickness,
        });
    }
    componentWillUnmount() {
        // Remove all paper elements.
        this.arrowHead.remove();
        this.line.remove();
        this.hittableLine.remove();
        this.advancedText.remove();
        this.arrowHeadHint.remove();
        this.arrowTailHint.remove();
    }
    render() {
        var start = Pp(this.state.start);
        var end = Pp(this.state.end);
        // Displacement vector, points from start to end.
        let vector = end.subtract(start);
        this.line.segments[0].point = start;
        // Shorten the end by a bit so it does not overlap the arrow head.
        let lineLength = Math.max(0, vector.length - 10.0);
        this.line.segments[1].point = start.add(vector.normalize().multiply(lineLength));
        // Copy the segments over to the hittable line.
        this.hittableLine.segments[0].point = this.line.segments[0].point;
        this.hittableLine.segments[1].point = this.line.segments[1].point;
        this.arrowHead.position = end;
        this.arrowHead.rotation = vector.angle;
        this.arrowHead.scaling.x = clamp(vector.length / 20.0, 0.1, 1.0);
        // Set color.
        let arrowColor = "black";
        this.line.strokeColor = arrowColor;
        this.arrowHead.fillColor = arrowColor;
        // Set text colour.
        let textColor = this.state.textColor;
        this.textStyle.fillColor = textColor;
        // Set alignment.
        this.textStyle.textAlign = this.props.textAlign;
        // Selection outline.
        this.advancedText.container.selected = this.isSelected && !Debug.PRESENTATION_MODE;
        // Update label text. This needs to be done first before we update its position.
        // Note: Since this relies on setting the style, it needs to be called
        // after the line that sets up the text color.
        this.textStyle.fontSize = this.state.fontSize * 2;
        this.textStyle.fontFamily = this.props.fontFamily;
        let formattedText = getFormattedText(this.textCache, this.state.label, this.props.isChemMarkEnabled);
        this.advancedText.setText(formattedText, this.textStyle);
        // Place the text at the start of the arrow.
        // Note: This has to happen *after* setText.
        this.advancedText.setPosition(start.x, start.y);
        this.adjustLabel(vector.angle);
        if (this.props.endStyle == "none") {
            this.arrowHead.fillColor = SharedColors.transparent;
        }
        // Set thickness.
        this.line.strokeWidth = this.state.arrowThickness;
        this.hittableLine.strokeWidth = this.state.arrowThickness + 17.0;
        const arrowScale = this.state.arrowThickness / 3.0;
        this.arrowHead.scaling = P(arrowScale, arrowScale);
        // Adjust hint. Only show it when the arrow is selected.
        this.arrowHeadHint.position = end;
        this.arrowHeadHint.visible = this.props.showAdjustables;
        this.arrowTailHint.position = start;
        this.arrowTailHint.visible = this.props.showAdjustables;
        this.maybeUpdateUiElementsForZoom();
        // Show/Hide arrow.
        if (this.props.showArrow) {
            this.line.visible = true;
            this.hittableLine.visible = true;
            this.arrowHead.visible = true;
        }
        else {
            this.line.visible = false;
            this.hittableLine.visible = false;
            this.arrowHead.visible = false;
            this.arrowHeadHint.visible = false;
            this.arrowTailHint.visible = false;
        }
        return null;
    }
    // Adjust the label's properties based on the arrow's angle,
    // so that it does not overlap the arrow.
    adjustLabel(angle) {
        // Add some padding around the label.
        let bounds = this.advancedText.bounds.expand(P(20, 8));
        let vector = intersectCenterRayWithBox(bounds.width, bounds.height, angle);
        this.advancedText.container.position.x -= vector.x;
        this.advancedText.container.position.y -= vector.y;
    }
    // Draw an arrow, pointing to the right.
    createArrowHead() {
        let path = new Path(Segments([[0, -8]], [[0, 8]], [[20, 0]]));
        path.pivot = path.segments[2].point;
        path.closePath();
        return path;
    }
    maybeUpdateUiElementsForZoom() {
        if (this.props.zoom != this.lastUpdatedUiForZoom) {
            const zoom = this.props.zoom;
            const newScaling = P(1 / zoom, 1 / zoom);
            this.arrowHeadHint.scaling = newScaling;
            this.arrowTailHint.scaling = newScaling;
            this.lastUpdatedUiForZoom = zoom;
        }
    }
    forceUpdate(callback) {
        super.forceUpdate(callback);
        // Reset text cache to allow rerender.
        this.textCache.input = "";
    }
}
/**
 * Returns the formatted text to render.
 */
function getFormattedText(cache, text, isChemMarkEnabled) {
    if (!isChemMarkEnabled) {
        // Check the cache.
        if (cache.mode != "basic" || cache.input != text) {
            cache.mode = "basic";
            cache.input = text;
            cache.result = ChemMark.parseBasic(text);
        }
        return cache.result;
    }
    let config = appStore.getState().chemMark;
    if (!appStore.getState().tier) {
        if (cache.mode != "cmlimited" || cache.input != text) {
            cache.mode = "cmlimited";
            cache.input = text;
            cache.result = ChemMark.parseSmartLimited(text, config);
        }
        return cache.result;
    }
    if (cache.mode != "cm" || cache.input != text) {
        cache.mode = "cm";
        cache.input = text;
        cache.result = ChemMark.parseSmart(text, config);
    }
    return cache.result;
}
/**
 * Computes the vector from the center of a box to its edge.
 * Used to position the text box at the end of an arrow
 * @param angle Angle in degrees, range = [-180, 180]
 */
function intersectCenterRayWithBox(width, height, angleDeg) {
    // Assuming fixed x, compute what y would be.
    let candidateX = (angleDeg >= -90 && angleDeg <= 90) ? width / 2 : -width / 2;
    let candidateY = Math.tan(angleDeg.toRadians()) * candidateX;
    // If resulting y is within the box, the intersection occurs on the left or right side.
    if (candidateY <= height / 2 && candidateY >= -height / 2) {
        return P(candidateX, candidateY);
    }
    // Otherwise it occurs on the top or bottom side.
    let y = (angleDeg >= 0) ? height / 2 : -height / 2;
    let x = y / Math.tan(angleDeg.toRadians());
    return P(x, y);
}
